/**
 * RoleGuard Component - Warunkowe renderowanie na podstawie ról
 * wojskaspecjalne.pl
 */

import React from 'react';
import { useUserRole } from '../hooks/useUserRole';
import type { RoleName, Permission } from '../types/database.types';

interface RoleGuardProps {
  children: React.ReactNode;
  roles?: RoleName[]; // Użytkownik musi mieć którąkolwiek z tych ról
  permissions?: Permission[]; // Użytkownik musi mieć którekolwiek z tych uprawnień
  requireAll?: boolean; // Jeśli true, wymaga wszystkich ról/uprawnień, nie tylko jednego
  fallback?: React.ReactNode; // Wyświetl jeśli użytkownik nie ma dostępu
  loading?: React.ReactNode; // Wyświetl podczas ładowania
}

/**
 * Komponent warunkowego renderowania na podstawie ról użytkownika
 * 
 * @example
 * // Tylko dla adminów
 * <RoleGuard roles={['admin']}>
 *   <AdminPanel />
 * </RoleGuard>
 * 
 * @example
 * // Dla adminów lub moderatorów
 * <RoleGuard roles={['admin', 'moderator']}>
 *   <ModerationTools />
 * </RoleGuard>
 * 
 * @example
 * // Na podstawie uprawnień
 * <RoleGuard permissions={['forum.moderate']}>
 *   <ModerateButton />
 * </RoleGuard>
 */
export function RoleGuard({
  children,
  roles = [],
  permissions = [],
  requireAll = false,
  fallback = null,
  loading = (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-white-80)' }}>
      Ładowanie uprawnień...
    </div>
  ),
}: RoleGuardProps) {
  const { roles: userRoles, permissions: userPermissions, loading: isLoading } = useUserRole();

  console.log('[RoleGuard] Checking access:', {
    requiredRoles: roles,
    requiredPermissions: permissions,
    userRoles: userRoles.map(r => r.role_name),
    userPermissions,
    isLoading,
    requireAll
  });

  if (isLoading) {
    console.log('[RoleGuard] Still loading, showing loading fallback');
    return <>{loading}</>;
  }

  // Sprawdź role
  let hasRequiredRole = false;
  if (roles.length > 0) {
    if (requireAll) {
      hasRequiredRole = roles.every((role) =>
        userRoles.some((userRole) => userRole.role_name === role)
      );
    } else {
      hasRequiredRole = roles.some((role) =>
        userRoles.some((userRole) => userRole.role_name === role)
      );
    }
  } else {
    hasRequiredRole = true; // Brak wymaganych ról = zawsze true
  }

  // Sprawdź uprawnienia
  let hasRequiredPermission = false;
  if (permissions.length > 0) {
    if (requireAll) {
      hasRequiredPermission = permissions.every((permission) =>
        userPermissions.includes(permission)
      );
    } else {
      hasRequiredPermission = permissions.some((permission) =>
        userPermissions.includes(permission)
      );
    }
  } else {
    hasRequiredPermission = true; // Brak wymaganych uprawnień = zawsze true
  }

  console.log('[RoleGuard] Access check result:', {
    hasRequiredRole,
    hasRequiredPermission,
    granted: hasRequiredRole && hasRequiredPermission
  });

  // Wyświetl dzieci tylko jeśli użytkownik ma wymagane role i uprawnienia
  if (hasRequiredRole && hasRequiredPermission) {
    console.log('[RoleGuard] ✅ Access granted, rendering children');
    return <>{children}</>;
  }

  console.log('[RoleGuard] ❌ Access denied, showing fallback');
  return <>{fallback}</>;
}

/**
 * Komponent tylko dla adminów
 */
export function AdminOnly({
  children,
  fallback = null,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  return (
    <RoleGuard roles={['admin']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
}

/**
 * Komponent tylko dla moderatorów (i adminów)
 */
export function ModeratorOnly({
  children,
  fallback = null,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  return (
    <RoleGuard roles={['admin', 'moderator']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
}

/**
 * Komponent tylko dla zalogowanych użytkowników
 */
export function AuthenticatedOnly({
  children,
  fallback = null,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  return (
    <RoleGuard roles={['user', 'moderator', 'admin']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
}

